import { ReactNode } from "react";
import logo from "@/assets/wealthy-steward-logo.png";
import { addressSettled } from "@/lib/onboardingComplete";

export const ONBOARDING_PHASES = [
  "Your info",
  "Agreement",
  "Address",
  "Finish",
] as const;

export type OnboardingPhase = 1 | 2 | 3 | 4;

type PhaseRecord = {
  una_name_status?: string | null;
  una_name_confirmed_at?: string | null;
  mea_status?: string | null;
  ca_address_option?: string | null;
  suite_number?: number | null;
  lease_status?: string | null;
  payment_status?: string | null;
};

/**
 * Which of the four phases the member is standing in, read from the member record
 * the pages already load. Intake is phase one and is passed in directly.
 * Lease and payment are not universal, so they live inside the Address phase.
 */
export function resolveOnboardingPhase(record: PhaseRecord | null | undefined): OnboardingPhase {
  if (!record) return 1;

  const nameOpen =
    record.una_name_status === "submitted" || record.una_name_status === "change_requested";
  const meaOpen = record.mea_status !== "signed";
  const confirmOpen = !record.una_name_confirmed_at;
  if (nameOpen || meaOpen || confirmOpen) return 2;

  // Payment is never a phase gate. The suite path finishes at the lease.
  const option = record.ca_address_option;
  const addressFullyResolved = option === "self_sourced" || addressSettled(record);

  return addressFullyResolved ? 4 : 3;
}

type OnboardingShellProps = {
  currentPhase: OnboardingPhase;
  stepCaption?: string;
  children: ReactNode;
};

export function OnboardingShell({ currentPhase, stepCaption, children }: OnboardingShellProps) {
  return (
    <div className="min-h-[100dvh] bg-muted/20 pb-[env(safe-area-inset-bottom)]">
      <div className="mx-auto w-full max-w-2xl px-4 pb-16 pt-[26px] sm:pt-[68px]">
        <div className="flex flex-col items-center">
          <img
            src={logo}
            alt="Wealthy Steward"
            className="h-24 w-auto"
            loading="eager"
          />

          <div className="mt-[26px] w-full">
            <div className="flex items-start justify-between gap-2 sm:gap-3">
              {ONBOARDING_PHASES.map((label, index) => {
                const phaseNumber = index + 1;
                const done = phaseNumber < currentPhase;
                const current = phaseNumber === currentPhase;
                return (
                  <div key={label} className="flex min-w-0 flex-1 flex-col items-center gap-2">
                    <span
                      className={`h-1.5 w-full rounded-full transition-colors ${
                        done || current ? "bg-primary" : "bg-muted-foreground/25"
                      }`}
                    />
                    <span
                      className={`w-full truncate text-center text-[11px] leading-tight sm:text-xs ${
                        current
                          ? "font-medium text-primary"
                          : done
                          ? "text-muted-foreground"
                          : "text-muted-foreground/70"
                      }`}
                    >
                      {label}
                    </span>
                  </div>
                );
              })}
            </div>
            {stepCaption ? (
              <p className="mt-2 text-center text-xs text-muted-foreground">{stepCaption}</p>
            ) : null}
          </div>
        </div>

        {/* Generous card interiors: 20px on phones, 32px from small up. */}
        <div className="mt-[42px] [&_.p-6]:p-5 sm:[&_.p-6]:p-8 [&_.pt-0]:pt-0">{children}</div>
      </div>
    </div>
  );
}
